// src/components/AnnouncementForm.tsx
import React, { useState, useEffect } from 'react';
import { X, Megaphone, Check } from 'lucide-react';
import { Announcement } from '../types/announcement';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Announcement>) => void;
  initialData?: Announcement | null;
  isLoading?: boolean;
}

// Backend stores an ISO datetime; the <input type="date"> wants YYYY-MM-DD.
const toDateInput = (iso?: string | null) => (iso ? String(iso).slice(0, 10) : '');

export function AnnouncementForm({ isOpen, onClose, onSubmit, initialData, isLoading }: Props) {
  const [title, setTitle] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');   // YYYY-MM-DD or ''
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setIsActive(initialData.is_active !== false);
      setExpiresAt(toDateInput(initialData.expires_at));
      setSortOrder(initialData.sort_order || 0);
    } else {
      setTitle(''); setIsActive(true); setExpiresAt(''); setSortOrder(0);
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      body: '',   // single-text announcement: the message lives in `title`
      is_active: isActive,
      // Send a full-day ISO instant, or null to clear. Always send so PUT can unset.
      expires_at: expiresAt ? new Date(expiresAt + 'T23:59:59').toISOString() : null,
      sort_order: Number(sortOrder) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0f2d1f]/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col border border-[#e8e3d8]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e8e3d8] bg-[#faf8f3]">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-[#e8efe9] text-[#0f2d1f] flex items-center justify-center">
              <Megaphone className="w-4 h-4" />
            </span>
            <h3 className="text-[17px] font-semibold text-[#1a1a1a]">
              {initialData ? 'Edit announcement' : 'New announcement'}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 text-[#8a8580] hover:text-[#1a1a1a] hover:bg-[#f4f1ea] rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className="block text-xs font-medium text-[#8a8580] mb-1.5">Announcement</label>
            <textarea
              value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus rows={3} maxLength={255}
              placeholder="e.g. Office closed on 30 June for maintenance"
              className="w-full px-3.5 py-3 text-[14px] leading-relaxed text-[#1a1a1a] bg-white border border-[#d6d0c2] rounded-lg outline-none focus:border-[#0f2d1f] focus:ring-2 focus:ring-[#0f2d1f]/10 transition resize-y min-h-[88px]"
            />
            <p className="mt-1.5 text-[11px] text-[#a39e95]">This text scrolls in the header and shows in the “View more” popup.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#8a8580] mb-1.5">Auto-hide after (optional)</label>
              <input
                type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full px-3.5 py-2.5 text-[14px] text-[#1a1a1a] bg-white border border-[#d6d0c2] rounded-lg outline-none focus:border-[#0f2d1f] focus:ring-2 focus:ring-[#0f2d1f]/10 transition"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#8a8580] mb-1.5">Order (lower shows first)</label>
              <input
                type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))}
                className="w-full px-3.5 py-2.5 text-[14px] text-[#1a1a1a] bg-white border border-[#d6d0c2] rounded-lg outline-none focus:border-[#0f2d1f] focus:ring-2 focus:ring-[#0f2d1f]/10 transition"
              />
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <button type="button" role="switch" aria-checked={isActive} onClick={() => setIsActive((v) => !v)}
              className={`relative w-10 h-6 rounded-full transition-colors ${isActive ? 'bg-[#0f2d1f]' : 'bg-[#d6d0c2]'}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-4' : ''}`} />
            </button>
            <span className="text-[13px] text-[#4a4a4a]">
              <span className="font-medium text-[#1a1a1a]">{isActive ? 'Active' : 'Off'}</span>
              {' — '}{isActive ? 'visible in everyone’s header' : 'hidden from the header'}
            </span>
          </label>
        </form>

        <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-[#e8e3d8] bg-[#faf8f3]">
          <button type="button" onClick={onClose}
            className="px-4 py-2.5 text-[13.5px] font-medium text-[#4a4a4a] border border-[#d6d0c2] rounded-lg hover:bg-white transition">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit as any} disabled={isLoading || !title.trim()}
            className="flex items-center gap-1.5 px-5 py-2.5 text-[13.5px] font-medium text-white bg-[#0f2d1f] rounded-lg hover:bg-[#0a1f15] disabled:opacity-50 disabled:cursor-not-allowed transition">
            <Check className="w-4 h-4" />
            {isLoading ? 'Saving…' : initialData ? 'Save changes' : 'Create announcement'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnnouncementForm;
